import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import Swal from "sweetalert2";
import _ from "lodash";
import { useFormik } from "formik";
import * as Yup from "yup";

import { ws } from "src/services/ws";

// Tabs
import type { generalTab, sipTab, queuesTab } from "src/types";

// Store
import { useAppSelector, useAppDispatch } from "src/store/hooks";
import { setGeneral, setSip, setQueues } from "src/store/config/reducer";
import { initialState } from "src/store/config/reducer";

// Components
import Topbar from "src/pages/main/home/components/topbar";
import IcomBtn from "src/components/icom-btn";
import Tabs, { TabItem } from "src/components/tabs";

// Tabs
import General from "./tabs/general";
import SIP from "./tabs/sip";
import Queues from "./tabs/queues";
import { AppStatus } from "src/store/context/reducer";
import { trackEvent } from "src/services/logger";

// Formik schema validation
const validationSchema = Yup.object({
  paUploadServerUrl: Yup.string().url("URL inválida"),
});

export default function ConfigScreen() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { appStatus } = useAppSelector((state) => state.context);
  const { paused } = useAppSelector((state) => state.auth);

  // Last configuration saved
  const config = useAppSelector((state) => state.config);
  const configPassword = "@m&#2019";

  const savedConfig = {
    general: config.general,
    sip: config.sip,
    queues: config.queues,
  };

  useEffect(() => {
    ws?.sendMessage({
      type: "PA_SET_STATUS_PAUSE",
      data: { pauseType: "Tela de Configurações" },
    });

    return () => {
      if (appStatus === AppStatus.IN_PAUSE && paused) {
        ws?.sendMessage({
          type: "PA_SET_STATUS_PAUSE",
          data: { pauseType: paused?.texto },
        });
      } else {
        ws?.sendMessage({
          type: "PA_SET_STATUS_AVAILABLE",
          data: {
            isWaitingTransfer: false,
          },
        });
      }
    };
  }, []);

  // Tabs Redux States
  const general = useAppSelector((state) => state.config.general);
  const sip = useAppSelector((state) => state.config.sip);
  const queue = useAppSelector((state) => state.config.queues);
  // const endpoint = useAppSelector((state) => state.config.endpoints);

  // Inner state
  const [state, setState] = useState({
    loading: true,
  });
  const [hasChange, setHasChange] = useState(false);
  const [errors, setErrors] = useState(false);

  // General tab handlers and state
  const [generalState, setGeneralState] = useState<generalTab>(general);
  const handleGeneralSettings = (settings: generalTab) => {
    setGeneralState((prev) => ({
      ...prev,
      ...settings,
    }));
  };

  // SIP tab handlers and state
  const [sipState, setSipState] = useState<sipTab>(sip);
  const handleSipSettings = (settings: sipTab) => {
    setSipState((prev) => ({
      ...prev,
      ...settings,
    }));
  };

  // Queue tab handlers and state
  const [queuesState, setQueuesState] = useState<queuesTab>(queue);
  const handleQueuesSettings = (settings: queuesTab) => {
    setQueuesState(settings);
  };

  const saveConfig = () => {
    // Dispatch to store
    if (errors || !hasChange) return;

    if (!_.isEqual(generalState, initialState.general) || !_.isEqual(generalState, savedConfig.general)) {
      dispatch(setGeneral(generalState));
    }

    if (!_.isEqual(sipState, initialState.sip) || !_.isEqual(sipState, savedConfig.sip)) {
      dispatch(setSip(sipState));
    }

    if (!_.isEqual(queuesState, initialState.queues) || !_.isEqual(queuesState, savedConfig.queues)) {
      dispatch(setQueues(queuesState));
    }

    Swal.fire({
      title: "Configurações salvas com sucesso!",
      icon: "success",
      confirmButtonText: "Ok",
      customClass: {
        title: "dark:text-neutral-gray-50!",
        popup: "dark:bg-neutral-gray-900",
        actions: "dark:bg-neutral-gray-800!",
        inputLabel: "dark:text-neutral-gray-50!",
        input: "dark:text-neutral-gray-50! dark:bg-neutral-gray-800 dark:border-neutral-gray-800",
        confirmButton: "dark:bg-primary-blue-400 dark:text-white",
      },
    });
    trackEvent("settings_saved");
    setHasChange(false);
  };

  const changeRegister = () => {
    setHasChange(true);
  };

  // Formik Config
  const formik = useFormik({
    initialValues: {
      paUploadServerUrl: "",
    },
    validationSchema: validationSchema,
    onSubmit: () => {
      alert("submit");
      saveConfig();
    },
  });

  useEffect(() => {
    // Request password
    async function init() {
      const { value: password } = await Swal.fire({
        title: "Digite a senha para acessar o painel de configuração",
        input: "password",
        inputLabel: "Senha",
        inputPlaceholder: "•  •  •  •  •  •  •  •  •  •  •  •",
        showCancelButton: false,
        showCloseButton: true,
        closeButtonHtml: "&times;",
        confirmButtonText: "Entrar",
        customClass: {
          title: "dark:text-neutral-gray-50!",
          popup: "dark:bg-neutral-gray-900",
          actions: "dark:bg-neutral-gray-800!",
          inputLabel: "dark:text-neutral-gray-50!",
          input: "dark:text-neutral-gray-50! dark:bg-neutral-gray-800 dark:border-neutral-gray-800",
          confirmButton: "dark:bg-primary-blue-400 dark:text-white",
        },
        inputValidator: (value) => {
          if (!value) {
            return "Você precisa digitar a senha!";
          }
        },
      });

      if (password !== configPassword) return navigate("/");

      return setState((s) => ({ ...s, loading: false }));
    }
    init();
  }, []);

  useEffect(() => {
    setErrors(!!formik.errors.paUploadServerUrl);
  }, [formik.errors.paUploadServerUrl]);

  return (
    <div className="dark:bg-neutral-gray-900 page-config screen grow flex flex-col">
      <Topbar />
      {state.loading ? (
        "Carregando..."
      ) : (
        <form onSubmit={formik.handleSubmit}>
          <div className="flex flex-col px-8 pb-8 pt-4">
            <header className="flex justify-between">
              <h1 className="dark:text-white text-neutral-gray-900 font-sora text-3xl font-bold">Configurações</h1>
              <IcomBtn type="submit" size="large" disabled={errors || !hasChange} className="dark:bg-primary-blue-400 dark:text-white rounded-md!">
                Salvar
              </IcomBtn>
            </header>

            <Tabs className="pt-4">
              <TabItem label="Geral">
                <General currentConfig={generalState} setConfig={handleGeneralSettings} changeRegister={changeRegister} />
              </TabItem>
              <TabItem label="SIP">
                <SIP currentConfig={sipState} setConfig={handleSipSettings} changeRegister={changeRegister} />
              </TabItem>
              <TabItem label="Fila">
                <Queues currentConfig={queuesState} setConfig={handleQueuesSettings} changeRegister={changeRegister} />
              </TabItem>
            </Tabs>
          </div>
        </form>
      )}
    </div>
  );
}
